// ─── JSON-LD generators for PSEO pages ───────────────────────────────────────
// Spec: https://schema.org — validated against Google Rich Results Test.
// Three schema types per page kind:
//   - BreadcrumbList     (all kinds)
//   - FAQPage            (industry + use-case + comparison)
//   - SoftwareApplication (comparison pages)

import type { PseoPage } from '@/features/marketing/lib/pseo/pseo-core';
import type { FaqItem } from '@/data/pseo/content';
import { SITE_URL as BASE_URL } from '@/shared/lib/config';

// ─── BreadcrumbList ──────────────────────────────────────────────────────────

function breadcrumbJsonLd(page: PseoPage): object {
  const isComparison = page.kind === 'comparison';
  const isUseCase    = page.kind === 'use-case';

  const items = [
    { '@type': 'ListItem', position: 1, name: 'Home',      item: BASE_URL },
  ];

  if (isComparison) {
    items.push({ '@type': 'ListItem', position: 2, name: 'Compare', item: `${BASE_URL}/compare` });
    items.push({ '@type': 'ListItem', position: 3, name: page.title, item: page.canonical });
  } else if (isUseCase) {
    items.push({ '@type': 'ListItem', position: 2, name: 'AI Mascot Use Cases', item: `${BASE_URL}/ai-avatar/use-case` });
    items.push({ '@type': 'ListItem', position: 3, name: page.title, item: page.canonical });
  } else {
    items.push({ '@type': 'ListItem', position: 2, name: 'AI Mascot by Industry', item: `${BASE_URL}/ai-avatar` });
    items.push({ '@type': 'ListItem', position: 3, name: page.title, item: page.canonical });
  }

  return {
    '@context': 'https://schema.org',
    '@type':    'BreadcrumbList',
    itemListElement: items,
  };
}

// ─── FAQPage ─────────────────────────────────────────────────────────────────

function faqJsonLd(faqs: FaqItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type':    'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type':          'Question',
      name:             f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text:    f.answer,
      },
    })),
  };
}

// ─── SoftwareApplication (comparison pages) ──────────────────────────────────

function softwareAppJsonLd(page: PseoPage): object {
  return {
    '@context':          'https://schema.org',
    '@type':             'SoftwareApplication',
    name:                'Minimoes',
    applicationCategory: 'BusinessApplication',
    operatingSystem:     'Web',
    url:                 BASE_URL,
    description:         page.description,
    offers: {
      '@type':        'Offer',
      name:           'Free Plan',
      price:          '0',
      priceCurrency:  'USD',
      url:            `${BASE_URL}/#pricing`,
      availability:   'https://schema.org/InStock',
    },
  };
}

// ─── Public builder ───────────────────────────────────────────────────────────

export interface PseoJsonLd {
  breadcrumb:  object;
  faq?:        object;
  software?:   object;
}

export function buildPseoJsonLd(page: PseoPage, faqs?: FaqItem[]): PseoJsonLd {
  const result: PseoJsonLd = {
    breadcrumb: breadcrumbJsonLd(page),
  };

  if (faqs && faqs.length > 0) {
    result.faq = faqJsonLd(faqs);
  }

  if (page.kind === 'comparison') {
    result.software = softwareAppJsonLd(page);
  }

  return result;
}
